import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './Cart.css';
import { formatInrWhole } from '../utils/money';
import { STORE_WHATSAPP_E164, whatsappUrl } from '../utils/whatsapp';

function productUrl(jewelId) {
  const path = `/jewel/${jewelId}`;
  if (typeof window === 'undefined' || !window.location?.origin) return path;
  return `${window.location.origin}${path}`;
}

export default function Cart() {
  const { items, removeItem, updateQuantity, clearCart, totalValue } = useCart();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        name,
        phone,
        state,
        pincode,
        items: items.map(i => ({ jewelId: i.jewelId, quantity: i.quantity })),
      };

      const res = await fetch('/api/enquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let data;
      try { data = await res.json(); } catch { data = {}; }

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit enquiry');
      }

      const enquiryId = data.enquiryId;
      const messageLines = [
        `New enquiry #${enquiryId}`,
        '',
        `Name: ${name}`,
        `Phone: ${phone}`,
        state ? `State: ${state}` : null,
        pincode ? `Pincode: ${pincode}` : null,
        '',
        'Items:',
        ...items.flatMap((i) => [
          `- (ID ${i.jewelId}) ${i.name} x${i.quantity}`,
          `  Product: ${productUrl(i.jewelId)}`,
        ]),
      ].filter(Boolean);
      const waLink = whatsappUrl(STORE_WHATSAPP_E164, messageLines.join('\n'));
      try {
        sessionStorage.setItem('lastEnquiryWhatsAppUrl', waLink);
      } catch {
        // ignore storage issues (private mode, etc.)
      }

      clearCart();
      navigate(`/enquiry-confirmed/${enquiryId}`);

      // Best-effort: open WhatsApp after DB save. Some browsers may block popups.
      try {
        window.open(waLink, '_blank', 'noopener,noreferrer');
      } catch {
        // ignore
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cart-page">
      <h1>Your Enquiry Cart</h1>

      {items.length === 0 ? (
        <div className="cart-empty">
          <p>Your cart is empty</p>
          <Link to="/" className="btn btn-primary">Browse Jewellery</Link>
        </div>
      ) : (
        <div className="cart-layout">
          <div className="cart-items">
            {items.map(item => (
              <div key={item.jewelId} className="cart-item">
                <div className="cart-item-img">
                  {item.image_mime_type ? (
                    <img src={`/api/jewels/${item.jewelId}/image`} alt={item.name} />
                  ) : (
                    <div className="cart-item-placeholder">No Image</div>
                  )}
                </div>
                <div className="cart-item-details">
                  <Link to={`/jewel/${item.jewelId}`} className="cart-item-name">{item.name}</Link>
                  <p className="cart-item-material">{item.material}</p>
                  {item.display_price != null && (
                    <p className="cart-item-price">{formatInrWhole((item.display_price || 0) * item.quantity)}</p>
                  )}
                </div>
                <div className="cart-item-qty">
                  <button onClick={() => updateQuantity(item.jewelId, item.quantity - 1)} disabled={item.quantity <= 1}>−</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.jewelId, item.quantity + 1)}>+</button>
                </div>
                <button className="cart-item-remove" onClick={() => removeItem(item.jewelId)}>✕</button>
              </div>
            ))}

            <div className="cart-total">
              <span>Estimated Total:</span>
              <span>{formatInrWhole(totalValue)}</span>
            </div>
            <p className="cart-note">* Prices are indicative based on live rates. Final price confirmed upon enquiry.</p>
          </div>

          <div className="cart-form">
            <h2>Submit Enquiry</h2>
            {error && <p className="cart-error">{error}</p>}
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group">
                <label htmlFor="eq-name">Name *</label>
                <input id="eq-name" type="text" name="enquiry_customer_name" value={name} onChange={e => setName(e.target.value)} required autoComplete="name" />
              </div>
              <div className="form-group">
                <label htmlFor="eq-phone">Phone Number *</label>
                <input
                  id="eq-phone"
                  type="tel"
                  name="enquiry_phone"
                  inputMode="numeric"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  pattern="[0-9]{10}"
                  title="10-digit phone number"
                  placeholder=" "
                  className="cart-input-sparse"
                  autoComplete="off"
                />
                <span className="cart-field-hint">10 digits, no spaces</span>
              </div>
              <div className="form-group">
                <label htmlFor="eq-state">State</label>
                <input
                  id="eq-state"
                  type="text"
                  name="enquiry_state"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  placeholder=" "
                  className="cart-input-sparse"
                  autoComplete="off"
                />
                <span className="cart-field-hint">Optional</span>
              </div>
              <div className="form-group">
                <label htmlFor="eq-pincode">Pincode</label>
                <input
                  id="eq-pincode"
                  type="text"
                  name="enquiry_pin"
                  inputMode="numeric"
                  value={pincode}
                  onChange={e => setPincode(e.target.value)}
                  pattern="[0-9]{6}"
                  title="6-digit pincode"
                  placeholder=" "
                  className="cart-input-sparse"
                  autoComplete="off"
                />
                <span className="cart-field-hint">6 digits, optional</span>
              </div>
              <button type="submit" className="btn btn-primary submit-btn" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Send Enquiry'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
